import { AutomationDeliveryProvider } from "@repo/app-types/entities/automation";
import {
  describeAutomationReportConfig,
  type AutomationRunInterpretation,
} from "@repo/app-types/entities/automationInterpretation";
import type { GroupId } from "@repo/app-types/entities/group";
import type { TeammateId } from "@repo/app-types/entities/teammate";
import { renderTeammateDisplayName } from "@repo/app-types/entities/teammate";
import { automationDeliveryProviderLabels } from "@repo/ui/automations/automationPresentation";
import { ChatToolBase, ChatToolDisclosure } from "@repo/ui/chat/chatToolCall";
import { useMemo, type FC } from "react";
import { useAllTeammateGroupsQuery } from "../../api/entities/group.js";
import { useAllTeammatesQuery } from "../../api/entities/teammate.js";

export interface RunInterpretationStepProps {
  interpretation: AutomationRunInterpretation;
}

/**
 * How this run read its automation's prompt, rendered as the first progress row of
 * the run's conversation — the same check/cross rows its tool calls render as.
 *
 * A prompt-defined automation has no configuration page stating what its results
 * will be, so this is the only place an editor can see what their instructions
 * were understood to mean — and a preview run is how they check that before a
 * scheduled run delivers on it. Warnings sit behind the row's disclosure
 * rather than replacing it: the run still produced everything it could.
 */
export const RunInterpretationStep: FC<RunInterpretationStepProps> = (
  props,
) => {
  const { interpretation } = props;
  // Specific selections in the reading are stored as ids; resolve them to
  // display names so the summary can enumerate who was selected.
  const { data: teammates } = useAllTeammatesQuery();
  const { data: groups } = useAllTeammateGroupsQuery();
  const teammateNamesById = useMemo(
    () =>
      new Map<TeammateId, string>(
        (teammates ?? []).map((teammate) => [
          teammate.id,
          renderTeammateDisplayName(teammate),
        ]),
      ),
    [teammates],
  );
  const groupNamesById = useMemo(
    () =>
      new Map<GroupId, string>(
        (groups ?? []).map((group) => [group.id, group.name]),
      ),
    [groups],
  );

  if (interpretation.status === "unresolved") {
    return (
      <ChatToolDisclosure
        status={{ type: "incomplete" }}
        label="Could not read the instructions"
      >
        <p className="text-xs text-muted-foreground">{interpretation.error}</p>
      </ChatToolDisclosure>
    );
  }

  // Mid-sentence, "email" is a common noun while "Slack"/"Microsoft Teams"
  // are proper nouns, so only email drops its display-label capitalization.
  const provider = interpretation.transport.provider;
  const providerLabel =
    provider === AutomationDeliveryProvider.EMAIL
      ? "email"
      : automationDeliveryProviderLabels[provider];
  const label = `Interpreted report plan from prompt: ${describeAutomationReportConfig(
    interpretation.report_definition,
    providerLabel,
    { teammateNamesById, groupNamesById },
  )}`;

  if (interpretation.warnings.length === 0) {
    return <ChatToolBase status={{ type: "complete" }}>{label}</ChatToolBase>;
  }
  return (
    <ChatToolDisclosure status={{ type: "complete" }} label={label}>
      <ul className="list-disc space-y-1 pl-5 text-xs text-muted-foreground">
        {interpretation.warnings.map((warning) => (
          <li key={warning}>{warning}</li>
        ))}
      </ul>
    </ChatToolDisclosure>
  );
};
